import { ActionLogEntityType, ActionLogModule } from "@/Api/actionLogs";

export interface LogActionOption {
  id: number;
  key: string;
  label: string;
  module: ActionLogModule;
}

export interface ActionLogOptions {
  modules: { value: ActionLogModule; label: string }[];
  actions: LogActionOption[];
  users: { id: number; name: string | null }[];
  sellers: { id: number; name: string | null }[];
  unities: { id: number; name: string | null }[];
}

export interface ActionLogRow {
  id: number;
  uuid: string;
  log_action_id: number;
  user_id: number | null;
  user_name: string | null;
  entity_type: ActionLogEntityType;
  entity_id: number | null;
  entity_uuid: string | null;
  entity_label: string | null;
  seller_id: number | null;
  unity_id: number | null;
  seller: { id: number; user: { id: number; name: string } | null } | null;
  unity: { id: number; name: string } | null;
  description: string | null;
  ip_address: string | null;
  created_at: string;
  log_action: LogActionOption;
}

export interface ActionLogDetail extends ActionLogRow {
  metadata: Record<string, unknown> | null;
  route: string | null;
  method: string | null;
  user_agent: string | null;
  user: { id: number; uuid: string; name: string; email: string } | null;
}

export interface Paginated<T> {
  data: T[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

export const MODULE_LABELS: Record<ActionLogModule, string> = {
  contests: "Concursos",
  sales: "Vendas",
};

export const formatDateTime = (value: string | null | undefined) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

/** Link para o registro afetado, quando a tela de destino suporta abrir por id. */
export const entityLink = (row: Pick<ActionLogRow, "entity_type" | "entity_id" | "log_action">) => {
  if (!row.entity_id) return null;

  if (row.entity_type === "sale") {
    return `/vendas?sale_id=${row.entity_id}`;
  }

  // Concurso excluído não tem mais tela para abrir.
  if (row.entity_type === "contest" && row.log_action?.key !== "contest.deleted") {
    return `/concursos?q=${btoa(String(row.entity_id))}`;
  }

  return null;
};

const badgeTone = (color: string) =>
  `border-transparent ${color} hover:opacity-90`;

/** Uma cor por ação do catálogo (chave = log_actions.key). */
const ACTION_BADGE_CLASSES: Record<string, string> = {
  "contest.created": badgeTone(
    "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
  ),
  "contest.updated": badgeTone(
    "bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300"
  ),
  "contest.canceled": badgeTone(
    "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300"
  ),
  "contest.finished": badgeTone(
    "bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300"
  ),
  "contest.status_changed": badgeTone(
    "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
  ),
  "contest.investigation_created": badgeTone(
    "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300"
  ),
  "contest.deleted": badgeTone(
    "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300"
  ),
  "sale.cancel_requested": badgeTone(
    "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300"
  ),
  "sale.cancel_approved": badgeTone(
    "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300"
  ),
  "sale.cancel_rejected": badgeTone(
    "bg-pink-100 text-pink-800 dark:bg-pink-950 dark:text-pink-300"
  ),
  "sale.canceled_directly": badgeTone(
    "bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
  ),
  "pre_sale.cancel_requested": badgeTone(
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300"
  ),
  "pre_sale.cancel_approved": badgeTone(
    "bg-lime-100 text-lime-800 dark:bg-lime-950 dark:text-lime-300"
  ),
  "pre_sale.cancel_rejected": badgeTone(
    "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950 dark:text-fuchsia-300"
  ),
};

export const actionBadgeClass = (key: string) =>
  ACTION_BADGE_CLASSES[key] ?? badgeTone("bg-secondary text-secondary-foreground");

export interface ParsedUserAgent {
  browser: string;
  os: string;
  device: string;
}

/** Interpreta o user agent para exibição (sem depender de biblioteca). */
export const parseUserAgent = (ua: string): ParsedUserAgent => {
  const match = (regex: RegExp) => ua.match(regex)?.[1];

  const browsers: [string, RegExp][] = [
    ["Edge", /Edg(?:e|A|iOS)?\/([\d.]+)/],
    ["Opera", /OPR\/([\d.]+)/],
    ["Firefox", /(?:Firefox|FxiOS)\/([\d.]+)/],
    ["Chrome", /(?:Chrome|CriOS)\/([\d.]+)/],
    ["Safari", /Version\/([\d.]+).*Safari/],
  ];
  const found = browsers.find(([, regex]) => regex.test(ua));
  const browser = found
    ? `${found[0]} ${match(found[1])?.split(".")[0] ?? ""}`.trim()
    : "Desconhecido";

  let os = "Desconhecido";
  if (/Windows NT/.test(ua)) os = "Windows";
  else if (/Android/.test(ua)) os = `Android ${match(/Android ([\d.]+)/) ?? ""}`.trim();
  else if (/iPhone|iPad|iPod/.test(ua))
    os = `iOS ${match(/OS ([\d_]+)/)?.replace(/_/g, ".") ?? ""}`.trim();
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/CrOS/.test(ua)) os = "ChromeOS";
  else if (/Linux/.test(ua)) os = "Linux";

  let device = "Computador";
  if (/iPad|Tablet/.test(ua)) device = "Tablet";
  else if (/Mobi|iPhone|Android/.test(ua)) device = "Celular";

  return { browser, os, device };
};
